import React, { useState } from "react";
import { useSearchParams, useOutletContext } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";

const Contact = () => {
  const { t } = useI18n();
  const { siteSettings } = useOutletContext() || {};
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", country: "", email: "", whatsapp: "",
    interest: "", quantity: "", destination: "", message: "",
    product_id: params.get("product") || "",
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      toast.success(t("rfq.success"));
      setForm({ name: "", company: "", country: "", email: "", whatsapp: "", interest: "", quantity: "", destination: "", message: "", product_id: "" });
    } catch (err) {
      toast.error("Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const contact = siteSettings?.contact || {};

  return (
    <div data-testid="contact-page">
      <section className="bg-[#0A1128] text-white py-24 md:py-28">
        <div className="container-nakia">
          <div className="flex items-center gap-3 mb-5">
            <span className="gold-divider" />
            <span className="text-eyebrow text-[#D4AF37]">RFQ</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl max-w-3xl">{t("rfq.title")}</h1>
          <p className="text-white/75 max-w-2xl mt-6">{t("rfq.subtitle")}</p>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-white">
        <div className="container-nakia grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Form */}
          <form onSubmit={onSubmit} className="lg:col-span-7 space-y-5" data-testid="rfq-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label={t("rfq.name")} name="name" value={form.name} onChange={onChange} required testId="rfq-name" />
              <Field label={t("rfq.company")} name="company" value={form.company} onChange={onChange} testId="rfq-company" />
              <Field label={t("rfq.country")} name="country" value={form.country} onChange={onChange} testId="rfq-country" />
              <Field label={t("rfq.email")} name="email" type="email" value={form.email} onChange={onChange} required testId="rfq-email" />
              <Field label={t("rfq.whatsapp")} name="whatsapp" value={form.whatsapp} onChange={onChange} testId="rfq-whatsapp" />
              <Field label={t("rfq.interest")} name="interest" value={form.interest} onChange={onChange} testId="rfq-interest" />
              <Field label={t("rfq.quantity")} name="quantity" value={form.quantity} onChange={onChange} testId="rfq-quantity" />
              <Field label={t("rfq.destination")} name="destination" value={form.destination} onChange={onChange} testId="rfq-destination" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">{t("rfq.message")}</label>
              <textarea
                name="message" rows={5} value={form.message} onChange={onChange}
                data-testid="rfq-message"
                className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-sm"
              />
            </div>
            <button type="submit" disabled={submitting} data-testid="rfq-submit" className="btn-gold disabled:opacity-50">
              <Send size={14} /> {submitting ? "..." : t("rfq.send")}
            </button>
          </form>

          {/* Contact info */}
          <aside className="lg:col-span-5">
            <div className="bg-[#0A1128] text-white p-10 rounded-sm">
              <h3 className="font-display text-2xl mb-7">{t("nav.contact")}</h3>
              <ul className="space-y-5 text-sm">
                {contact.address && (
                  <li className="flex gap-3"><MapPin size={18} className="text-[#D4AF37] mt-0.5 shrink-0" /><span>{contact.address}</span></li>
                )}
                {contact.email && (
                  <li className="flex gap-3"><Mail size={18} className="text-[#D4AF37] mt-0.5 shrink-0" /><a href={`mailto:${contact.email}`} className="hover:text-[#D4AF37]">{contact.email}</a></li>
                )}
                {contact.phone && (
                  <li className="flex gap-3"><Phone size={18} className="text-[#D4AF37] mt-0.5 shrink-0" /><a href={`tel:${contact.phone}`} className="hover:text-[#D4AF37]">{contact.phone}</a></li>
                )}
                {contact.whatsapp && (
                  <li className="flex gap-3"><MessageCircle size={18} className="text-[#D4AF37] mt-0.5 shrink-0" /><a href={`https://wa.me/${contact.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37]">{contact.whatsapp}</a></li>
                )}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required, testId }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">{label}{required && " *"}</label>
    <input
      type={type} name={name} value={value} onChange={onChange} required={required}
      data-testid={testId}
      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-sm"
    />
  </div>
);

export default Contact;
